import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const statsRef = useRef<HTMLDivElement>(null);
  const [counts, setCounts] = useState({ articles: 0, categories: 0, readers: 0 });
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated) {
          setAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [animated]);

  useEffect(() => {
    if (!animated) return;
    const targets = { articles: 1520, categories: 6, readers: 86 };
    const duration = 2000;
    const start = Date.now();

    const timer = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCounts({
        articles: Math.floor(targets.articles * easeOut),
        categories: Math.floor(targets.categories * easeOut),
        readers: Math.floor(targets.readers * easeOut),
      });
      if (progress >= 1) clearInterval(timer);
    }, 30);

    return () => clearInterval(timer);
  }, [animated]);

  return (
    <section className="relative w-full min-h-[600px] md:min-h-[700px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20warm%20minimal%20gradient%20background%20with%20soft%20organic%20shapes%20in%20cream%20beige%20and%20warm%20terracotta%20tones%2C%20clean%20elegant%20aesthetic%2C%20subtle%20texture%2C%20editorial%20style%20with%20gentle%20light%20diffusion&width=1800&height=900&seq=hero-bg-main&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/10 to-black/20"></div>
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 py-20 md:py-28">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm mb-6">
            <i className="ri-sparkling-line"></i>
            <span>2026年8月 · 本周精选更新</span>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-6">
            精品一区 · 优选内容汇聚地
          </h1>

          <p className="text-lg md:text-xl text-white/85 leading-relaxed mb-8 max-w-2xl">
            精品一区是您发现高质量精选内容的首选平台，汇聚严选优质资源，为您提供极致体验。无论您追求品质生活、精品内容还是优选服务，精品一区都能满足您的高端需求。
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/category/keji-qianyan"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              立即探索
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/15 backdrop-blur-sm text-white font-medium rounded-md hover:bg-white/25 transition-colors duration-200 whitespace-nowrap cursor-pointer border border-white/20"
            >
              了解更多
              <i className="ri-information-line"></i>
            </Link>
          </div>
        </div>

        <div ref={statsRef} className="grid grid-cols-3 gap-4 md:gap-8 mt-14 md:mt-20 max-w-xl">
          {[
            { value: counts.articles, suffix: '+', label: '精选文章' },
            { value: counts.categories, suffix: ' 大', label: '内容分类' },
            { value: counts.readers, suffix: '万+', label: '忠实读者' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-white mb-1">
                {stat.value}{stat.suffix}
              </div>
              <div className="text-sm text-white/70">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background-50 to-transparent"></div>
    </section>
  );
}